// askSmart-ui/src/pages/TOSP/TospDataContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import {
  deleteRecord,
  listRecords,
  loadConfiguration,
  saveConfiguration as putConfiguration,
  saveRecord,
  unlockBudget as unlockBudgetRecord,
  Stored,
  TospApiError,
} from "./tospApi";
import {
  Building,
  BudgetYear,
  DEFAULT_CONFIGURATION,
  normalizeConfiguration,
  normalizeOccupancy,
  normalizeTenant,
  normalizeUnit,
  normalizeTaxRoll,
  Occupancy,
  TaxRollRecord,
  TaxYear,
  Tenant,
  TospConfiguration,
  Unit,
} from "./types";

// Occupancies and tax years are their own documents on the backend — a
// takeover then writes one lease rather than two whole tenants, and "every
// roll for 2026" is a single query. The UI keeps them nested where they read
// best, so this context is where the two shapes meet: merged on the way out,
// split on the way in.
type StoredTenant = Stored<Omit<Tenant, "occupancies">>;
type StoredUnit = Stored<Omit<Unit, "taxYears">>;
type StoredOccupancy = Stored<Occupancy & { tenantId: string }>;
type StoredTaxYear = Stored<TaxYear & { unitId: string }>;

interface TospData {
  /** False until the first load completes. */
  ready: boolean;
  /** Set when loading or a save failed; shown to the admin rather than swallowed. */
  error: string | null;
  clearError: () => void;
  reload: () => Promise<void>;

  buildings: Building[];
  units: Unit[];
  tenants: Tenant[];
  configuration: TospConfiguration;

  saveBuilding: (building: Building) => Promise<void>;
  deleteBuilding: (id: string) => Promise<void>;
  saveUnit: (unit: Unit) => Promise<void>;
  deleteUnit: (id: string) => Promise<void>;
  saveTenant: (tenant: Tenant) => Promise<void>;
  deleteTenant: (id: string) => Promise<void>;

  /** The roll register — every assessed parcel, assigned or not. */
  taxRolls: TaxRollRecord[];
  /** Writes rolls one document at a time, so a partial failure leaves the
   *  ones that landed. Used by the seed and by every assignment. */
  saveTaxRolls: (rolls: TaxRollRecord[]) => Promise<void>;
  deleteTaxRoll: (id: string) => Promise<void>;
  /** Used by a takeover to end another tenant's lease. */
  saveOccupancy: (tenantId: string, occupancy: Occupancy) => Promise<void>;
  saveConfiguration: (configuration: TospConfiguration) => Promise<void>;

  budgets: BudgetYear[];
  /** Writes one budget — its lines, splits, history and revenue adjustments. */
  saveBudget: (budget: BudgetYear) => Promise<void>;
  deleteBudget: (id: string) => Promise<void>;
  /** Reopens a published budget, recording the reason. Rejects on failure. */
  unlockBudget: (id: string, reason: string, byName: string) => Promise<void>;

  /** Building name for an id, or "—" when the building is gone. */
  buildingName: (buildingId: string) => string;
  /**
   * How a unit is named wherever it appears beside others: "(S@B) C-1".
   *
   * The building's nickname, not its full name — a label read in a dropdown
   * or a grid has to stay short enough to scan, and "Seasons at Blue-C-1"
   * buries the part that identifies the unit. Bracketed so the two halves
   * don't run together into something that looks like one code.
   */
  unitLabel: (unit: Pick<Unit, "buildingId" | "suiteNumber">) => string;
  unitById: (unitId: string) => Unit | undefined;
}

const TospDataContext = createContext<TospData | null>(null);

const message = (err: unknown): string =>
  err instanceof TospApiError ? err.message : "Something went wrong. Please try again.";

export function TospDataProvider({ children }: { children: ReactNode }) {
  const [buildingRecords, setBuildingRecords] = useState<Stored<Building>[]>([]);
  const [unitRecords, setUnitRecords] = useState<StoredUnit[]>([]);
  const [taxRollRecords, setTaxRollRecords] = useState<Stored<TaxRollRecord>[]>([]);
  const [taxYearRecords, setTaxYearRecords] = useState<StoredTaxYear[]>([]);
  const [tenantRecords, setTenantRecords] = useState<StoredTenant[]>([]);
  const [occupancyRecords, setOccupancyRecords] = useState<StoredOccupancy[]>([]);
  const [configRecord, setConfigRecord] = useState<Stored<TospConfiguration> | null>(null);
  const [budgetRecords, setBudgetRecords] = useState<Stored<BudgetYear>[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [
        buildings,
        units,
        taxRolls,
        taxYears,
        tenants,
        occupancies,
        budgets,
        configuration,
      ] = await Promise.all([
        listRecords<Building>("buildings"),
        listRecords<Omit<Unit, "taxYears">>("units"),
        listRecords<TaxRollRecord>("tax-rolls"),
        listRecords<TaxYear & { unitId: string }>("tax-years"),
        listRecords<Omit<Tenant, "occupancies">>("tenants"),
        listRecords<Occupancy & { tenantId: string }>("occupancies"),
        listRecords<BudgetYear>("budgets"),
        loadConfiguration<TospConfiguration>(),
      ]);
      setBuildingRecords(buildings);
      setUnitRecords(units);
      setTaxRollRecords(taxRolls);
      setTaxYearRecords(taxYears);
      setTenantRecords(tenants);
      setOccupancyRecords(occupancies);
      setBudgetRecords(budgets);
      setConfigRecord(configuration);
      setError(null);
    } catch (err) {
      console.error("[TOSP] load failed:", err);
      setError(message(err));
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // ── Merged views ─────────────────────────────────────────────────────────
  // normalizeUnit / normalizeConfiguration still run, so documents written
  // before a shape changed are read forward.

  const units = useMemo<Unit[]>(
    () =>
      unitRecords.map((unit) =>
        normalizeUnit({
          ...(unit as unknown as Unit),
          taxYears: taxYearRecords
            .filter((year) => year.unitId === unit.id)
            .sort((a, b) => b.year - a.year),
        }),
      ),
    [unitRecords, taxYearRecords],
  );

  const tenants = useMemo<Tenant[]>(
    () =>
      tenantRecords.map((tenant) =>
        normalizeTenant({
          ...(tenant as unknown as Tenant),
            // Read forward here, once, so nothing downstream ever meets a
          // lease saved before Leased Spaces existed.
          occupancies: occupancyRecords
            .filter((o) => o.tenantId === tenant.id)
            .map(normalizeOccupancy),
        }),
      ),
    [tenantRecords, occupancyRecords],
  );

  const configuration = useMemo<TospConfiguration>(
    () => normalizeConfiguration({ ...DEFAULT_CONFIGURATION, ...(configRecord || {}) }),
    [configRecord],
  );

  // ── Writes ───────────────────────────────────────────────────────────────
  // Each returns the saved document so the new ETag replaces the old one; a
  // failed save leaves local state untouched and surfaces the message.

  const withError = async (work: () => Promise<void>) => {
    try {
      await work();
      setError(null);
    } catch (err) {
      console.error("[TOSP] save failed:", err);
      setError(message(err));
      throw err;
    }
  };

  const etagOf = <T extends { id: string }>(records: Stored<T>[], id: string) =>
    records.find((r) => r.id === id)?._etag;

  const saveBuilding = (building: Building) =>
    withError(async () => {
      const saved = await saveRecord<Building>("buildings", {
        ...building,
        _etag: etagOf(buildingRecords, building.id),
      });
      setBuildingRecords((prev) =>
        prev.some((b) => b.id === saved.id)
          ? prev.map((b) => (b.id === saved.id ? saved : b))
          : [...prev, saved],
      );
    });

  const deleteBuilding = (id: string) =>
    withError(async () => {
      // Units point at their building by id, so removing one underneath them
      // strands every unit it holds — and with them their rolls, tax years and
      // occupancies. The Buildings tab says so in full; the rule lives here too
      // so no other path can get round it.
      const held = unitRecords.filter((unit) => unit.buildingId === id);
      if (held.length > 0) {
        const building = buildingRecords.find((b) => b.id === id);
        throw new TospApiError(
          `${building?.name || "That building"} still holds ${held.length} unit${
            held.length === 1 ? "" : "s"
          }. Move or delete them before deleting the building.`,
          409,
        );
      }

      await deleteRecord("buildings", id);
      setBuildingRecords((prev) => prev.filter((b) => b.id !== id));
    });

  // A unit's tax years are separate documents: write the ones it has, remove
  // the ones it no longer does.
  const saveUnit = (unit: Unit) =>
    withError(async () => {
      const { taxYears, ...fields } = unit;
      const saved = await saveRecord<Omit<Unit, "taxYears">>("units", {
        ...(fields as Omit<Unit, "taxYears">),
        _etag: etagOf(unitRecords, unit.id),
      });

      // Banked before the tax years are written. If one of those fails, the
      // unit's new ETag is still recorded — otherwise the next save would send
      // the ETag this write just superseded and 409 until a reload.
      setUnitRecords((prev) =>
        prev.some((u) => u.id === saved.id)
          ? prev.map((u) => (u.id === saved.id ? saved : u))
          : [...prev, saved],
      );

      const savedYears = await Promise.all(
        (taxYears || []).map((year) =>
          saveRecord<TaxYear & { unitId: string }>("tax-years", {
            ...year,
            unitId: unit.id,
            _etag: etagOf(taxYearRecords, year.id),
          }).then((savedYear) => {
            // Banked individually, for the same reason.
            setTaxYearRecords((prev) =>
              prev.some((y) => y.id === savedYear.id)
                ? prev.map((y) => (y.id === savedYear.id ? savedYear : y))
                : [...prev, savedYear],
            );
            return savedYear;
          }),
        ),
      );

      const removed = taxYearRecords
        .filter((y) => y.unitId === unit.id && !savedYears.some((s) => s.id === y.id))
        .map((y) => y.id);
      await Promise.all(removed.map((id) => deleteRecord("tax-years", id)));
      if (removed.length > 0) {
        setTaxYearRecords((prev) => prev.filter((y) => !removed.includes(y.id)));
      }
    });

  const deleteUnit = (id: string) =>
    withError(async () => {
      // Same rule the Units tab states in full: a unit any tenant has occupied
      // stays. Deleting it would strand that occupancy and take the suite's
      // lease history with it. Repeated here so no other path can get round it.
      const occupied = occupancyRecords.some((o) =>
        (normalizeOccupancy(o).leasedSpaces || []).some((space) => space.unitId === id),
      );
      if (occupied) {
        const unit = unitRecords.find((u) => u.id === id);
        throw new TospApiError(
          `Suite ${unit?.suiteNumber || id} is assigned to a tenant. Remove the occupancy from the tenant before deleting the unit.`,
          409,
        );
      }

      await deleteRecord("units", id);
      await Promise.all(
        taxYearRecords
          .filter((y) => y.unitId === id)
          .map((y) => deleteRecord("tax-years", y.id)),
      );

      // The unit's roll parts go back to the pool rather than away with it —
      // the parcels still exist and are still billed, they just have no home.
      // Only the current allocation is touched; earlier years keep theirs.
      const orphaned = taxRollRecords
        .map((roll) => normalizeTaxRoll(roll))
        .filter((roll) =>
          roll.allocations[roll.allocations.length - 1].parts.some(
            (part) => part.assignedTo?.type === "unit" && part.assignedTo.id === id,
          ),
        );
      for (const roll of orphaned) {
        const current = roll.allocations[roll.allocations.length - 1];
        const saved = await saveRecord<TaxRollRecord>("tax-rolls", {
          ...roll,
          allocations: [
            ...roll.allocations.slice(0, -1),
            {
              ...current,
              parts: current.parts.map((part) =>
                part.assignedTo?.id === id ? { ...part, assignedTo: null } : part,
              ),
            },
          ],
        });
        setTaxRollRecords((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
      }

      setUnitRecords((prev) => prev.filter((u) => u.id !== id));
      setTaxYearRecords((prev) => prev.filter((y) => y.unitId !== id));
    });

  const saveTenant = (tenant: Tenant) =>
    withError(async () => {
      const { occupancies, ...fields } = tenant;
      const saved = await saveRecord<Omit<Tenant, "occupancies">>("tenants", {
        ...(fields as Omit<Tenant, "occupancies">),
        _etag: etagOf(tenantRecords, tenant.id),
      });

      // Banked before the leases are written — see saveUnit.
      setTenantRecords((prev) =>
        prev.some((t) => t.id === saved.id)
          ? prev.map((t) => (t.id === saved.id ? saved : t))
          : [...prev, saved],
      );

      const savedOccupancies = await Promise.all(
        (occupancies || []).map((occupancy) =>
          saveRecord<Occupancy & { tenantId: string }>("occupancies", {
            ...occupancy,
            tenantId: tenant.id,
            _etag: etagOf(occupancyRecords, occupancy.id),
          }).then((lease) => {
            setOccupancyRecords((prev) =>
              prev.some((o) => o.id === lease.id)
                ? prev.map((o) => (o.id === lease.id ? lease : o))
                : [...prev, lease],
            );
            return lease;
          }),
        ),
      );

      const removed = occupancyRecords
        .filter(
          (o) => o.tenantId === tenant.id && !savedOccupancies.some((s) => s.id === o.id),
        )
        .map((o) => o.id);
      await Promise.all(removed.map((id) => deleteRecord("occupancies", id)));
      if (removed.length > 0) {
        setOccupancyRecords((prev) => prev.filter((o) => !removed.includes(o.id)));
      }
    });

  const deleteTenant = (id: string) =>
    withError(async () => {
      await deleteRecord("tenants", id);
      await Promise.all(
        occupancyRecords
          .filter((o) => o.tenantId === id)
          .map((o) => deleteRecord("occupancies", o.id)),
      );
      setTenantRecords((prev) => prev.filter((t) => t.id !== id));
      setOccupancyRecords((prev) => prev.filter((o) => o.tenantId !== id));
    });

  // One lease on its own — what a takeover needs, since the outgoing tenant is
  // otherwise untouched.
  const saveOccupancy = (tenantId: string, occupancy: Occupancy) =>
    withError(async () => {
      const saved = await saveRecord<Occupancy & { tenantId: string }>("occupancies", {
        ...occupancy,
        tenantId,
        _etag: etagOf(occupancyRecords, occupancy.id),
      });
      setOccupancyRecords((prev) =>
        prev.some((o) => o.id === saved.id)
          ? prev.map((o) => (o.id === saved.id ? saved : o))
          : [...prev, saved],
      );
    });

  // Rolls are written one document at a time — an import of 152 that fails
  // partway leaves the ones that landed, and two admins assigning two
  // different rolls touch two different documents and never contend.
  const saveTaxRolls = (rolls: TaxRollRecord[]) =>
    withError(async () => {
      for (const roll of rolls) {
        const saved = await saveRecord<TaxRollRecord>("tax-rolls", {
          ...roll,
          _etag: etagOf(taxRollRecords, roll.id),
        });
        setTaxRollRecords((prev) =>
          prev.some((r) => r.id === saved.id)
            ? prev.map((r) => (r.id === saved.id ? saved : r))
            : [...prev, saved],
        );
      }
    });

  const deleteTaxRoll = (id: string) =>
    withError(async () => {
      // The register's grid disables the button, but the rule belongs here too:
      // a roll a unit is taxed on can't be deleted out from under it, whichever
      // path asks. Only the current allocation counts — an earlier year's
      // assignment is history, not a live claim.
      const roll = taxRollRecords.find((r) => r.id === id);
      if (roll) {
        const current = normalizeTaxRoll(roll).allocations.slice(-1)[0];
        if (current.parts.some((part) => part.assignedTo)) {
          throw new TospApiError(
            `Roll ${roll.rollNumber} is assigned to a unit. Remove it from that unit before deleting.`,
            409,
          );
        }
      }

      await deleteRecord("tax-rolls", id);
      setTaxRollRecords((prev) => prev.filter((r) => r.id !== id));
    });

  // A budget is one document, written whole: its lines, splits, history and
  // revenue adjustments are all part of the same thing being worked on, and a
  // budget is edited by one admin at a sitting. The ETag still guards it, so
  // two people in the same budget get a 409 rather than one silently losing
  // their work.
  const saveBudget = (budget: BudgetYear) =>
    withError(async () => {
      const saved = await saveRecord<BudgetYear>("budgets", {
        ...budget,
        _etag: etagOf(budgetRecords, budget.id),
      });
      setBudgetRecords((prev) =>
        prev.some((b) => b.id === saved.id)
          ? prev.map((b) => (b.id === saved.id ? saved : b))
          : [...prev, saved],
      );
    });

  /**
   * Reopens a published budget. Throws rather than swallowing, so the modal
   * can keep the reason on screen and say what went wrong — a silent failure
   * here would leave the admin believing a locked budget was editable.
   */
  const unlockBudget = async (id: string, reason: string, byName: string) => {
    const saved = await unlockBudgetRecord<BudgetYear>(
      id,
      reason,
      byName,
      etagOf(budgetRecords, id),
    );
    setBudgetRecords((prev) => prev.map((b) => (b.id === saved.id ? saved : b)));
  };

  const deleteBudget = (id: string) =>
    withError(async () => {
      await deleteRecord("budgets", id);
      setBudgetRecords((prev) => prev.filter((b) => b.id !== id));
    });

  const saveConfiguration = (next: TospConfiguration) =>
    withError(async () => {
      const saved = await putConfiguration<TospConfiguration>({
        ...next,
        _etag: configRecord?._etag,
      } as Stored<TospConfiguration>);
      setConfigRecord(saved);
    });

  const value = useMemo<TospData>(
    () => ({
      ready,
      error,
      clearError: () => setError(null),
      reload,
      buildings: buildingRecords as Building[],
      units,
      tenants,
      configuration,
      saveBuilding,
      deleteBuilding,
      saveUnit,
      deleteUnit,
      saveTenant,
      deleteTenant,
      // Normalised on the way out, so rolls written before they could be
      // divided read forward as a single whole part.
      taxRolls: taxRollRecords.map((roll) => normalizeTaxRoll(roll)),
      saveTaxRolls,
      deleteTaxRoll,
      saveOccupancy,
      saveConfiguration,
      budgets: budgetRecords as BudgetYear[],
      saveBudget,
      deleteBudget,
      unlockBudget,
      buildingName: (buildingId) =>
        buildingRecords.find((b) => b.id === buildingId)?.name || "—",
      unitLabel: (unit) => {
        const building = buildingRecords.find((b) => b.id === unit.buildingId);
        // Falls back to the full name, then to nothing at all — a unit whose
        // building has been deleted still reads as its suite rather than as
        // an empty pair of brackets.
        const short = building?.nickname?.trim() || building?.name?.trim();
        return short ? `(${short}) ${unit.suiteNumber}` : unit.suiteNumber;
      },
      unitById: (unitId) => units.find((u) => u.id === unitId),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      ready,
      error,
      reload,
      buildingRecords,
      units,
      tenants,
      taxRollRecords,
      configuration,
      budgetRecords,
    ],
  );

  return <TospDataContext.Provider value={value}>{children}</TospDataContext.Provider>;
}

export function useTospData(): TospData {
  const ctx = useContext(TospDataContext);
  if (!ctx) throw new Error("useTospData must be used within a TospDataProvider");
  return ctx;
}
